import React, { createContext, useContext, useEffect, useState } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { auth } from '../config/firebase';
import { peekTenantRuntime, hasDashboardRole } from '../config/tenant';
import type { User, UserRole } from '../types';
interface AuthContextType {
  identity: FirebaseUser | null; user: User | null; loading: boolean; error: string | null;
  login(email: string, password: string): Promise<void>; logout(): Promise<void>;
  hasRole(role: UserRole | UserRole[]): boolean; getAuthToken(): Promise<string | null>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function useAuth() {
  const context = useContext(AuthContext); if (!context) throw new Error('AuthProvider fehlt');
  const runtime = peekTenantRuntime();
  return { ...context, user: context.identity && runtime ? { uid: context.identity.uid, email: context.identity.email ?? '', displayName: context.identity.displayName ?? undefined, role: runtime.role } : null };
}
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [identity, setIdentity] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => onAuthStateChanged(auth, user => { setIdentity(user); setLoading(false); }), []);
  return <AuthContext.Provider value={{ identity, user: null, loading, error,
    login: async (email, password) => { setError(null); try { await signInWithEmailAndPassword(auth, email, password); } catch (error) { setError(error instanceof Error ? error.message : 'Anmelden fehlgeschlagen'); throw error; } },
    logout: async () => { await signOut(auth); setIdentity(null); },
    hasRole: role => { const runtime = peekTenantRuntime(); return !!runtime && hasDashboardRole(runtime, role); },
    getAuthToken: async () => auth.currentUser ? auth.currentUser.getIdToken() : null,
  }}>{children}</AuthContext.Provider>;
}
